import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, BookOpen, User, Palette, GraduationCap, Layout, Sparkles, Users, ArrowRightLeft } from 'lucide-react';

interface LandingNavbarProps {
    onGetStarted: () => void;
    onLogoClick?: () => void;
    onLogin?: () => void;
}

export const LandingNavbar: React.FC<LandingNavbarProps> = ({ onGetStarted, onLogoClick }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [currentPath, setCurrentPath] = useState(window.location.pathname);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };
        const handlePathChange = () => {
            setCurrentPath(window.location.pathname);
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('popstate', handlePathChange);

        // Custom event for internal navigation if needed
        window.addEventListener('pushstate', handlePathChange);
        window.addEventListener('replacestate', handlePathChange);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('popstate', handlePathChange);
            window.removeEventListener('pushstate', handlePathChange);
            window.removeEventListener('replacestate', handlePathChange);
        };
    }, []);

    const isActive = (path: string) => {
        if (path === '/' && currentPath === '/') return true;
        if (path !== '/' && currentPath.startsWith(path)) return true;
        return false;
    };

    const linkClass = (path: string) => `
        flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-all rounded-full
        ${isActive(path)
            ? 'text-white bg-white/10 shadow-[0_0_15px_rgba(255,255,255,0.05)]'
            : 'text-slate-300 hover:text-white hover:bg-white/5'}
    `;

    const dropdownItemClass = (path: string) => `
        flex items-start gap-3 p-2 rounded-lg transition-colors group/item
        ${isActive(path) ? 'bg-white/10 ring-1 ring-white/10' : 'hover:bg-white/5'}
    `;

    return (
        <>
            {/* Desktop Floating Island Navbar */}
            <motion.nav
                initial={{ y: -100, x: "-50%", opacity: 0 }}
                animate={{ y: 0, x: "-50%", opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className={`fixed top-6 left-1/2 z-50 hidden md:flex items-center justify-between gap-1 p-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] ring-1 ring-white/5 transition-all duration-300 ${isScrolled ? 'scale-100 shadow-[0_4px_20px_rgba(255,255,255,0.05)]' : 'scale-[1.02]'}`}
                style={{
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                    backdropFilter: 'blur( 16px )',
                    WebkitBackdropFilter: 'blur( 16px )',
                }}
            >
                {/* Logo */}
                <a
                    href="/"
                    onClick={(e) => {
                        e.preventDefault();
                        window.history.pushState({}, '', '/');
                        onLogoClick?.();
                    }}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors group ${isActive('/') ? 'bg-white/10' : 'hover:bg-white/5'}`}
                >
                    <img src="/notescape.png" alt="NoteSpace" className={`w-6 h-6 rounded-md group-hover:scale-110 transition-transform shadow-lg group-hover:shadow-indigo-500/20 ${isActive('/') ? 'grayscale-0' : 'grayscale group-hover:grayscale-0'}`} />
                    <span className={`font-semibold tracking-tight text-sm transition-colors ${isActive('/') ? 'text-indigo-200' : 'text-white group-hover:text-indigo-200'}`}>NoteSpace</span>
                </a>

                <div className="w-px h-5 bg-white/10 mx-1" />

                {/* Links */}
                <div className="flex items-center gap-1">

                    {/* Features Dropdown */}
                    <div className="relative group/menu">
                        <button className={linkClass('/features')}>
                            Features <ChevronDown className={`w-3 h-3 opacity-50 group-hover/menu:rotate-180 transition-transform ${isActive('/features') ? 'rotate-180 opacity-100' : ''}`} />
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover/menu:opacity-100 group-hover/menu:translate-y-0 group-hover/menu:pointer-events-auto transition-all duration-300 ease-out">
                            <div className="bg-[#121217] border border-white/10 rounded-xl p-2 w-[320px] shadow-2xl backdrop-blur-3xl">
                                <a href="/features/infinite-canvas" className={dropdownItemClass('/features/infinite-canvas')}>
                                    <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center group-hover/item:bg-orange-500/20 transition-colors">
                                        <Layout className="w-4 h-4 text-orange-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">Infinite Canvas</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Spatial freedom for your ideas</div>
                                    </div>
                                </a>
                                <a href="/features/ai-study-tools" className={dropdownItemClass('/features/ai-study-tools')}>
                                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center group-hover/item:bg-purple-500/20 transition-colors">
                                        <Sparkles className="w-4 h-4 text-purple-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">AI Study Tools</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Summaries, quizzes & flashcards</div>
                                    </div>
                                </a>
                                <a href="/features/collaboration" className={dropdownItemClass('/features/collaboration')}>
                                    <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center group-hover/item:bg-green-500/20 transition-colors">
                                        <Users className="w-4 h-4 text-green-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">Real-time Collaboration</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Work together instantly</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Solutions Dropdown */}
                    <div className="relative group/menu">
                        <button className={linkClass('/use-cases')}>
                            Solutions <ChevronDown className={`w-3 h-3 opacity-50 group-hover/menu:rotate-180 transition-transform ${isActive('/use-cases') ? 'rotate-180 opacity-100' : ''}`} />
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover/menu:opacity-100 group-hover/menu:translate-y-0 group-hover/menu:pointer-events-auto transition-all duration-300 ease-out">
                            <div className="bg-[#121217] border border-white/10 rounded-xl p-2 w-[280px] shadow-2xl backdrop-blur-3xl">
                                <a href="/use-cases/students" className={dropdownItemClass('/use-cases/students')}>
                                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center group-hover/item:bg-indigo-500/20 transition-colors">
                                        <GraduationCap className="w-4 h-4 text-indigo-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">For Students</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Ace exams with AI flashcards</div>
                                    </div>
                                </a>
                                <a href="/use-cases/designers" className={dropdownItemClass('/use-cases/designers')}>
                                    <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center group-hover/item:bg-pink-500/20 transition-colors">
                                        <Palette className="w-4 h-4 text-pink-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">For Designers</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Infinite moodboards & ideation</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Compare Dropdown */}
                    <div className="relative group/menu">
                        <button className={linkClass('/compare')}>
                            Compare <ChevronDown className={`w-3 h-3 opacity-50 group-hover/menu:rotate-180 transition-transform ${isActive('/compare') ? 'rotate-180 opacity-100' : ''}`} />
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover/menu:opacity-100 group-hover/menu:translate-y-0 group-hover/menu:pointer-events-auto transition-all duration-300 ease-out">
                            <div className="bg-[#121217] border border-white/10 rounded-xl p-2 w-[280px] shadow-2xl backdrop-blur-3xl">
                                <a href="/compare/notion-vs-notespace" className={dropdownItemClass('/compare/notion-vs-notespace')}>
                                    <div className="w-8 h-8 rounded-lg bg-stone-500/10 flex items-center justify-center group-hover/item:bg-stone-500/20 transition-colors">
                                        <ArrowRightLeft className="w-4 h-4 text-stone-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">vs Notion</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Why visual thinking wins</div>
                                    </div>
                                </a>
                                <a href="/compare/miro-vs-notespace" className={dropdownItemClass('/compare/miro-vs-notespace')}>
                                    <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center group-hover/item:bg-yellow-500/20 transition-colors">
                                        <ArrowRightLeft className="w-4 h-4 text-yellow-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">vs Miro</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Free alternative for study</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Resources Dropdown */}
                    <div className="relative group/menu">
                        <button className={linkClass('/blog')}>
                            Resources <ChevronDown className={`w-3 h-3 opacity-50 group-hover/menu:rotate-180 transition-transform ${isActive('/blog') ? 'rotate-180 opacity-100' : ''}`} />
                        </button>
                        <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 translate-y-2 pointer-events-none group-hover/menu:opacity-100 group-hover/menu:translate-y-0 group-hover/menu:pointer-events-auto transition-all duration-300 ease-out">
                            <div className="bg-[#121217] border border-white/10 rounded-xl p-2 w-[280px] shadow-2xl backdrop-blur-3xl">
                                <a href="/blog" className={dropdownItemClass('/blog')}>
                                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center group-hover/item:bg-blue-500/20 transition-colors">
                                        <BookOpen className="w-4 h-4 text-blue-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">Blog</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Explore all insights</div>
                                    </div>
                                </a>
                                <a href="/help" className={dropdownItemClass('/help')}>
                                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center group-hover/item:bg-emerald-500/20 transition-colors">
                                        <User className="w-4 h-4 text-emerald-400" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold text-slate-200">Help Center</div>
                                        <div className="text-[10px] text-slate-500 leading-tight">Guides & support</div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="pl-2 flex items-center gap-2">
                    <a
                        href="/download"
                        className={`px-5 py-2 rounded-full text-xs font-bold transition-all border border-white/10 tracking-wide ${isActive('/download') ? 'bg-white/20 text-white' : 'bg-white/10 hover:bg-white/20 text-white'}`}
                    >
                        Download
                    </a>
                    <button
                        onClick={onGetStarted}
                        className="px-5 py-2 rounded-full bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition-all shadow-lg shadow-indigo-500/20 active:scale-95 border border-white/10 tracking-wide"
                    >
                        Open App
                    </button>
                </div>
            </motion.nav>

            {/* Mobile Navbar */}
            <div className={`fixed top-0 left-0 right-0 z-50 md:hidden p-4 transition-all duration-300 ${isScrolled ? 'bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/10' : ''}`}>
                <div className="flex items-center justify-between">
                    <a href="/" className="flex items-center gap-2">
                        <img src="/notescape.png" alt="NoteSpace" className="w-8 h-8 rounded-lg" />
                        <span className="font-bold text-white tracking-tight">NoteSpace</span>
                    </a>
                    <button
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                        aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                    >
                        {isMobileMenuOpen ? <X /> : <Menu />}
                    </button>
                </div>

                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden bg-[#121217] border border-white/10 rounded-2xl mt-4 shadow-2xl"
                        >
                            <div className="p-4 flex flex-col gap-2">
                                {[
                                    { name: 'Features', href: '/features/infinite-canvas' },
                                    { name: 'For Students', href: '/use-cases/students' },
                                    { name: 'For Designers', href: '/use-cases/designers' },
                                    { name: 'Vs Notion', href: '/compare/notion-vs-notespace' },
                                    { name: 'Vs Miro', href: '/compare/miro-vs-notespace' },
                                    { name: 'Blog', href: '/blog' },
                                    { name: 'Download', href: '/download' },
                                ].map((link) => (
                                    <a
                                        key={link.name}
                                        href={link.href}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className={`p-3 rounded-xl transition-colors font-medium text-lg ${isActive(link.href) ? 'text-white bg-white/10' : 'text-slate-400 hover:text-white hover:bg-white/5'}`}
                                    >
                                        {link.name}
                                    </a>
                                ))}
                                <div className="h-px bg-white/10 my-2" />
                                <button
                                    onClick={() => {
                                        setIsMobileMenuOpen(false);
                                        onGetStarted();
                                    }}
                                    className="w-full py-3 rounded-xl bg-white text-black font-bold text-center shadow-lg hover:bg-zinc-200 transition-colors"
                                >
                                    Get Started
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </>
    );
};

